import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, Phone, Mail, Clock, Briefcase, Users, FileText, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    subject: [],
    message: ''
  });

  const subjects = [
    'Information générale',
    'Partenariat',
    'Investissement',
    'Carrière',
    'Presse',
    'Autre'
  ];

  const subsidiaries = [
    {
      name: 'KIP ENERGY',
      email: 'contact@kipenergy.ci',
      phone: '+225 27 22 41 25 25 | +225 05 06 06 55 06',
      icon: Phone,
      color: '#FF6600'
    },
    {
      name: 'KIPYZ DESIGN & HOUSES (KIDH)',
      email: 'contact@kidh.ci',
      phone: '+225 27 22 41 25 25 | +225 05 06 06 55 06',
      icon: Phone,
      color: '#00A651'
    },
    {
      name: 'EKIPTECH',
      email: 'contact@ekiptech.com',
      phone: '+225 27 22 41 25 25 | +225 05 06 06 55 06',
      icon: Phone,
      color: '#003366'
    },
    {
      name: 'KIPAY',
      email: 'contact@kipay.net',
      phone: '+225 27 22 41 25 25 | +225 05 06 06 55 06',
      icon: Phone,
      color: '#FFB81C'
    }
  ];

  const opportunities = [
    {
      icon: Briefcase,
      title: 'Carrières',
      description: 'Rejoignez un groupe en forte croissance',
      cta: 'Voir nos offres d\'emploi',
      color: '#FF6600'
    },
    {
      icon: Users,
      title: 'Partenariats',
      description: 'Collaborons pour créer de la valeur',
      cta: 'Contactez notre équipe',
      color: '#00A651'
    },
    {
      icon: FileText,
      title: 'Presse',
      description: 'Accédez à nos ressources média',
      cta: 'Espace Presse',
      color: '#003366'
    }
  ];

  const handleSubjectChange = (subject) => {
    setFormData(prev => ({
      ...prev,
      subject: prev.subject.includes(subject)
        ? prev.subject.filter(s => s !== subject)
        : [...prev.subject, subject]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive"
      });
      return;
    }

    // Show success message
    toast({
      title: "Message envoyé !",
      description: "Nous vous contacterons dans les plus brefs délais.",
    });

    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: '',
      subject: [],
      message: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contactez-Nous
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Nous sommes à votre écoute pour répondre à toutes vos questions
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-[#003366] mb-8">
                  Siège Social
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#FF6600] p-3 rounded-lg flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Adresse</h3>
                      <p className="text-gray-600">
                        Cocody 2 Plateaux Boulevard Latrille 49 Rue K104,<br />
                        Abidjan, Côte d'Ivoire
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-[#00A651] p-3 rounded-lg flex-shrink-0">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Téléphone</h3>
                      <p className="text-gray-600">+225 27 22 41 25 25</p>
                      <p className="text-gray-600">+225 05 06 06 55 06</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-[#003366] p-3 rounded-lg flex-shrink-0">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Email</h3>
                      <a href="mailto:contact@ekipholding.ci" className="text-[#FF6600] hover:underline block">
                        contact@ekipholding.ci
                      </a>
                      <a href="mailto:infoline@ekipholding.ci" className="text-[#FF6600] hover:underline block">
                        infoline@ekipholding.ci
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-[#FFB81C] p-3 rounded-lg flex-shrink-0">
                      <Clock className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Horaires</h3>
                      <p className="text-gray-600">Lundi - Vendredi: 8h00 - 17h00</p>
                      <p className="text-gray-600">Samedi: 8h00 - 12h00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-[#003366] shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#003366] mb-8">
                    Envoyez-nous un Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="company">Entreprise</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="mb-3 block">Sujet *</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {subjects.map((subject) => (
                          <div key={subject} className="flex items-center space-x-2">
                            <Checkbox
                              id={subject}
                              checked={formData.subject.includes(subject)}
                              onCheckedChange={() => handleSubjectChange(subject)}
                            />
                            <label
                              htmlFor={subject}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {subject}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-2"
                        placeholder="Votre message..."
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
                    >
                      <Send className="mr-2" size={20} />
                      Envoyer
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Subsidiaries Contact */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#003366] mb-6">
              Contacts par Filiale
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {subsidiaries.map((subsidiary, index) => {
              const Icon = subsidiary.icon;
              return (
                <Card key={index} className="border-2 hover:shadow-xl transition-shadow duration-300" style={{ borderColor: subsidiary.color }}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#003366] mb-4">{subsidiary.name}</h3>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <Mail className="flex-shrink-0 mt-1" style={{ color: subsidiary.color }} size={18} />
                        <a href={`mailto:${subsidiary.email}`} className="text-gray-700 hover:text-[#FF6600] transition-colors">
                          {subsidiary.email}
                        </a>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Icon className="flex-shrink-0 mt-1" style={{ color: subsidiary.color }} size={18} />
                        <span className="text-gray-700">{subsidiary.phone}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#003366] mb-6">
              Opportunités
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {opportunities.map((opportunity, index) => {
              const Icon = opportunity.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                  <CardContent className="p-8">
                    <div 
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                      style={{ backgroundColor: `${opportunity.color}15` }}
                    >
                      <Icon style={{ color: opportunity.color }} size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-[#003366] mb-3">{opportunity.title}</h3>
                    <p className="text-gray-600 mb-6">{opportunity.description}</p>
                    <Button 
                      variant="outline" 
                      className="border-2"
                      style={{ borderColor: opportunity.color, color: opportunity.color }}
                    >
                      {opportunity.cta}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};